import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProfileDropdown from "./ProfileDropdown";
import { getUserDetails } from "apis/homeServices";
import { getUserProfile, getProfilePicture } from "apis/settingsServices";
import { useUserStore } from "store/userStore";
import { extractAirlineCode } from "utils/stringUtils";
import NotificationBell from "assets/images/notification.png";
import AvatarPlaceholder from "assets/images/avatar-placeholder.svg";
import FavIcon from "assets/images/fav.png";
import StandbyLogo from "assets/images/standby.png";
import ReferralModal from "./ReferralCard";
import { subscribeToUserNotifications } from "config/firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { isUserSideRole } from "utils/roles";

const Header: React.FC = () => {
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const userRole = useUserStore((state) => state.role);
  const primaryAirlineCode = useUserStore((state) => state.primaryAirlineCode);
  const profilePictureUrl = useUserStore((state) => state.profilePictureUrl);

  // "Invite a Friend" and "Contact Us" are user-side only (regular users and
  // core-committee / community users). Admins see neither — just Log Out.
  const profileItems = isUserSideRole(userRole)
    ? [
        {
          label: "Invite a Friend",
          icon: "st-invite",
          onClick: () => setIsReferralModalOpen(true),
        },
        {
          label: "Contact Us",
          icon: "st-chat",
          onClick: () =>
            navigate("/settings?section=contactUs", {
              state: { section: "contactUs" },
            }),
        },
      ]
    : [];

  const [userDetails, setUserDetails] = useState<any>(() => {
    try {
      const savedUserDetails = localStorage.getItem("userDetails");
      return savedUserDetails ? JSON.parse(savedUserDetails) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      let savedUserDetails: string | null = null;
      try {
        savedUserDetails = localStorage.getItem("userDetails");
      } catch {
        // localStorage unavailable (e.g. Safari private mode); treat as no cached details
      }

      if (!savedUserDetails && !userDetails) {
        try {
          const response: any = await getUserDetails();
          setUserDetails(response);
          useUserStore.getState().setUsername(response.userName);
          useUserStore.getState().setEmail(response.email);
          useUserStore.getState().setUserRole(response.role);
          useUserStore.getState().setReferralCode(response.referralCode);
        } catch (error) {
          console.error("Failed to fetch user details:", error);
        }
      }
    };

    if (!userDetails) {
      fetchUserDetails();
    }
  }, [userDetails]);

  // Subscribe to notification unread count
  useEffect(() => {
    if (!isUserSideRole(userRole)) return;
    const auth = getAuth();
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        const unsubNotif = subscribeToUserNotifications(
          user.uid,
          (fetched) => {
            const count = fetched.filter((n: any) => !n.IsRead).length;
            setUnreadCount(count);
          }
        );
        return () => unsubNotif();
      } else {
        setUnreadCount(0);
      }
    });
    return () => unsubAuth();
  }, [userRole]);

  // Fetch user profile to get primary airline code
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profileData = await getUserProfile();
        if (profileData?.primaryAirline?.airlineName) {
          const airlineCode = extractAirlineCode(profileData.primaryAirline.airlineName);
          if (airlineCode) {
            useUserStore.getState().setPrimaryAirlineCode(airlineCode);
          }
        }
      } catch (error) {
        console.error("Failed to fetch user profile for airline code:", error);
      }
    };

    // Only fetch if we don't already have the airline code
    if (!primaryAirlineCode && isUserSideRole(userRole)) {
      fetchUserProfile();
    }
  }, [primaryAirlineCode, userRole]);

  // Fetch the profile picture into the store so the header avatar reflects the
  // user's uploaded photo (Settings updates the same store on upload). Only
  // fetch when it's not already cached from a previous session / upload.
  //
  // Every route builds its own MainLayout, so this component remounts on each
  // navigation — the cache check is what keeps that from firing a request per
  // page view. Logout clears the cache (see resetUserDetails), so the next user
  // re-fetches instead of inheriting this one's photo.
  useEffect(() => {
    const fetchProfilePicture = async () => {
      try {
        const data: any = await getProfilePicture();
        if (data?.profilePictureUrl) {
          useUserStore.getState().setProfilePictureUrl(data.profilePictureUrl);
        }
      } catch (error) {
        console.warn("No profile picture available", error);
      }
    };

    if (!profilePictureUrl) {
      fetchProfilePicture();
    }
  }, [profilePictureUrl]);

  return (
    <header className="relative z-30 bg-white border-b border-[#E5E7EB] px-6 py-3">
      <div className="flex items-center justify-between md:justify-end gap-3">
        {/* Logo — shown on mobile only (the sidebar carries it on desktop). */}
        <button
          type="button"
          onClick={() => navigate("/search")}
          className="md:hidden"
          aria-label="Standby home"
        >
          <img
            src={StandbyLogo}
            alt="Standby"
            className="h-8 w-auto object-contain"
          />
        </button>

        {/* Search icon */}
        {/* <button
          type="button"
          className="p-2 text-[#9CA3AF] hover:text-[#6B7280] hover:bg-[#F3F4F6] rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button> */}

        {/* Favourites */}
        {isUserSideRole(userRole) && (
          <button
            type="button"
            onClick={() => navigate("/favourites")}
            className="rounded-lg transition-colors"
            title="Favourites"
          >
            <img src={FavIcon} alt="Favourites" className="w-10 h-10 object-contain" />
          </button>
        )}

        {/* Notification */}
        {isUserSideRole(userRole) && (
          <button
            type="button"
            onClick={() => navigate("/notifications")}
            className="relative rounded-lg transition-colors"
            title="Notifications"
          >
            <img src={NotificationBell} alt="Notifications" className="w-10 h-10 object-contain" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
        )}

        {/* Profile */}
        <div className="border border-[#E5E7EB] rounded-full">
          <ProfileDropdown
            userAvatar={profilePictureUrl || AvatarPlaceholder}
            items={profileItems}
          />
        </div>
      </div>

      <ReferralModal
        isOpen={isReferralModalOpen}
        onClose={() => setIsReferralModalOpen(false)}
      />
    </header>
  );
};

export default Header;
